import { useState } from 'react';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { Dialog } from '@/ui/Dialog';
import { Field, InputSuelto, Textarea } from '@/ui/Field';
import { SkeletonTarjetas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { ContenidoRico } from '@/components/ContenidoRico';
import {
  useAccionDeModeracion, useAgregarPalabra, useBorrarPalabra, useModeracion,
  type ComentarioOculto, type ComentarioReportado, type PublicacionReportada,
} from './api';

/**
 * Moderación — denuncias, comentarios ocultos, historial y palabras bloqueadas.
 *
 * ## Lo que cambia
 *
 * - **El contenido denunciado ya no se inyecta como HTML.** `_commentReportsHtml`
 *   metía `${r.content}` y `${r.author_name}` en la plantilla **sin escapar**, y
 *   el historial hacía lo mismo con `${h.moderator}` y `${h.action_label}`. Es
 *   contenido escrito por quien publicó: exactamente el dato que no se puede
 *   interpolar. Aquí es texto porque es texto.
 * - **Las seis listas dicen si fallaron.** Se pedían con seis `try{}catch{}`
 *   **vacíos** seguidos, así que un error de red dejaba «Sin publicaciones
 *   reportadas. Todo está en orden por ahora» — el mensaje más tranquilizador
 *   posible sobre una consulta que no llegó a responder.
 * - **Las acciones piden su motivo dentro del mismo diálogo.** El vanilla abría
 *   un modal y le enganchaba el `onclick` con un `setTimeout(…, 60)` porque el
 *   botón todavía no existía en el DOM.
 * - **Ocultar ya no recarga toda la sección**: se invalida lo que cambió.
 */
export function Moderacion() {
  const m = useModeracion(true);
  const accion = useAccionDeModeracion();
  const [pidiendo, setPidiendo] = useState<
    { titulo: string; que: 'posts' | 'comments'; id: number; accion: 'hide' | 'restore' } | null>(null);

  function descartar(que: 'posts' | 'comments', id: number) {
    accion.mutate({ que, id, accion: 'dismiss' }, {
      onSuccess: () => Utils.toast('Denuncias descartadas', 'success'),
      onError: (e) => Utils.toast((e as Error).message, 'error'),
    });
  }

  return (
    <div className="flex flex-col gap-6">
      {m.politica.data?.policy && (
        <p className="flex items-start gap-2 rounded-sm border border-info bg-info/8 p-3 text-3 text-info-ink">
          <i className="bi bi-shield-shaded mt-0.5 shrink-0" aria-hidden="true" />
          <span><strong>Política de convivencia:</strong> {m.politica.data.policy}</span>
        </p>
      )}

      <Bloque
        icono="bi-flag-fill"
        titulo="Publicaciones reportadas"
        consulta={m.publicaciones}
        que="las publicaciones reportadas"
        vacio={{
          icono: 'bi-shield-check',
          titulo: 'Sin publicaciones reportadas',
          descripcion: 'Todo está en orden por ahora.',
        }}
      >
        {(filas: PublicacionReportada[]) => filas.map((r) => (
          <TarjetaDenunciada
            key={r.post_id}
            autor={r.author_name}
            contenido={r.content}
            comoHtml
            activo={r.is_active}
            reportes={r.report_count}
            motivos={r.reasons}
            acciones={
              <>
                {r.is_active ? (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setPidiendo({
                      titulo: 'Ocultar publicación', que: 'posts', id: r.post_id, accion: 'hide',
                    })}
                  >
                    <i className="bi bi-eye-slash" aria-hidden="true" /> Ocultar
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPidiendo({
                      titulo: 'Restaurar publicación', que: 'posts', id: r.post_id, accion: 'restore',
                    })}
                  >
                    <i className="bi bi-eye" aria-hidden="true" /> Restaurar
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => descartar('posts', r.post_id)}>
                  <i className="bi bi-check2" aria-hidden="true" /> Descartar denuncias
                </Button>
                <EnlaceALaPublicacion id={r.post_id} />
              </>
            }
          />
        ))}
      </Bloque>

      <Bloque
        icono="bi-chat-left-dots-fill"
        titulo="Comentarios reportados"
        consulta={m.comentarios}
        que="los comentarios reportados"
        vacio={{ icono: 'bi-shield-check', titulo: 'Sin comentarios reportados' }}
      >
        {(filas: ComentarioReportado[]) => filas.map((r) => (
          <TarjetaDenunciada
            key={r.comment_id}
            autor={r.author_name}
            contenido={r.content}
            activo={r.is_active}
            reportes={r.report_count}
            motivos={r.reasons}
            acciones={
              <>
                {r.is_active && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setPidiendo({
                      titulo: 'Ocultar comentario', que: 'comments', id: r.comment_id, accion: 'hide',
                    })}
                  >
                    <i className="bi bi-eye-slash" aria-hidden="true" /> Ocultar comentario
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => descartar('comments', r.comment_id)}
                >
                  <i className="bi bi-check2" aria-hidden="true" /> Descartar denuncias
                </Button>
                {r.post_id != null && <EnlaceALaPublicacion id={r.post_id} />}
              </>
            }
          />
        ))}
      </Bloque>

      <Bloque
        icono="bi-eye-slash-fill"
        titulo="Comentarios ocultos"
        ayuda="Ocultar un comentario cierra sus denuncias, así que deja de aparecer arriba. Esta lista es la única forma de volver atrás."
        consulta={m.ocultos}
        que="los comentarios ocultos"
        vacio={{ icono: 'bi-eye', titulo: 'Ningún comentario oculto' }}
      >
        {(filas: ComentarioOculto[]) => filas.map((r) => (
          <TarjetaDenunciada
            key={r.comment_id}
            autor={r.author_name}
            contenido={r.content}
            activo={false}
            motivo={r.motivo}
            acciones={
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPidiendo({
                    titulo: 'Restaurar comentario', que: 'comments', id: r.comment_id, accion: 'restore',
                  })}
                >
                  <i className="bi bi-eye" aria-hidden="true" /> Restaurar
                </Button>
                {r.post_id != null && <EnlaceALaPublicacion id={r.post_id} />}
              </>
            }
          />
        ))}
      </Bloque>

      <Historial consulta={m.historial} />
      <Palabras consulta={m.palabras} />

      {pidiendo && (
        <PedirMotivo
          titulo={pidiendo.titulo}
          cargando={accion.isPending}
          onCancelar={() => setPidiendo(null)}
          onConfirmar={(motivo) => accion.mutate(
            { que: pidiendo.que, id: pidiendo.id, accion: pidiendo.accion, motivo },
            {
              onSuccess: () => { Utils.toast('Hecho', 'success'); setPidiendo(null); },
              onError: (e) => Utils.toast((e as Error).message, 'error'),
            })}
        />
      )}
    </div>
  );
}

/** Un bloque de la sección, con su título, su estado de carga y su error. */
function Bloque<T>({
  icono, titulo, ayuda, consulta, que, vacio, children,
}: {
  icono: string;
  titulo: string;
  ayuda?: string;
  consulta: { data?: T[]; isPending: boolean; isError: boolean; error: unknown; refetch: () => unknown };
  que: string;
  vacio: { icono: string; titulo: string; descripcion?: string };
  children: (filas: T[]) => React.ReactNode;
}) {
  const filas = consulta.data ?? [];
  return (
    <section>
      <h3 className="flex items-center gap-2 text-3 font-bold text-brand-ink">
        <i className={`bi ${icono}`} aria-hidden="true" /> {titulo}
      </h3>
      {ayuda && <p className="mt-1 text-2 text-muted">{ayuda}</p>}
      <div className="mt-3">
        {consulta.isPending ? (
          <SkeletonTarjetas n={2} />
        ) : consulta.isError ? (
          <EstadoError err={consulta.error} que={que} onReintentar={() => void consulta.refetch()} />
        ) : !filas.length ? (
          <EstadoVacio icono={vacio.icono} titulo={vacio.titulo} descripcion={vacio.descripcion} />
        ) : (
          <ul className="flex flex-col gap-2">{children(filas)}</ul>
        )}
      </div>
    </section>
  );
}

function TarjetaDenunciada({
  autor, contenido, comoHtml, activo, reportes, motivos, motivo, acciones,
}: {
  autor: string;
  contenido: string;
  /** Una publicación trae menciones y enlaces ya saneados por el servidor; un
   *  comentario es texto plano. */
  comoHtml?: boolean;
  activo: boolean;
  reportes?: number;
  motivos?: string[];
  motivo?: string | null;
  acciones: React.ReactNode;
}) {
  return (
    <li>
      <Card className="p-3">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <span className="flex items-center gap-2 text-3 font-bold text-brand-ink">
            <i className="bi bi-person" aria-hidden="true" /> {autor}
            {!activo && <Badge tone="neutral">Oculto</Badge>}
          </span>
          {reportes != null && (
            <Badge tone="danger">
              <i className="bi bi-flag-fill" aria-hidden="true" /> {reportes}{' '}
              {reportes === 1 ? 'reporte' : 'reportes'}
            </Badge>
          )}
        </div>

        {comoHtml ? (
          // `ContenidoRico` y no un `dangerouslySetInnerHTML` propio: la capa
          // React tiene UN solo sitio que produce marcado, y es ese. Un segundo
          // punto de entrada al DOM sería otra lista blanca que mantener, y la
          // que se quedara atrás sería la que decide qué entra.
          <ContenidoRico html={contenido} className="mb-2 rounded-sm bg-sunken p-3 text-ink-2" />
        ) : (
          <p className="mb-2 whitespace-pre-wrap rounded-sm bg-sunken p-3 text-3 text-ink-2">
            {contenido}
          </p>
        )}

        {motivos?.length ? (
          <p className="mb-2 text-2 text-muted">
            <i className="bi bi-chat-left-text" aria-hidden="true" /> Motivos:{' '}
            {motivos.join(' · ')}
          </p>
        ) : null}
        {motivo && (
          <p className="mb-2 text-2 text-muted">
            <i className="bi bi-chat-quote" aria-hidden="true" /> Motivo: {motivo}
          </p>
        )}

        <div className="flex flex-wrap gap-2">{acciones}</div>
      </Card>
    </li>
  );
}

/** Ver la publicación. Es un enlace de verdad al muro y no un salto con
 *  `setTimeout` que buscaba el `.post-card` en el DOM —y que dejó de encontrarlo
 *  cuando el muro pasó a React—. */
function EnlaceALaPublicacion({ id }: { id: number }) {
  return (
    <a
      href={`#novedades/publicacion/${id}`}
      className="inline-flex items-center gap-1 px-3 py-1.5 text-2 font-semibold text-brand-ink no-underline hover:underline focus-visible:focus-anillo"
    >
      <i className="bi bi-box-arrow-up-right" aria-hidden="true" /> Ver publicación
    </a>
  );
}

const ICONO_DE_ACCION: Record<string, string> = {
  hide: 'bi-eye-slash text-danger-ink',
  restore: 'bi-eye text-ok-ink',
  dismiss: 'bi-check2',
};

function Historial({ consulta }: {
  consulta: ReturnType<typeof useModeracion>['historial'];
}) {
  const filas = consulta.data ?? [];
  return (
    <section>
      <h3 className="flex items-center gap-2 text-3 font-bold text-brand-ink">
        <i className="bi bi-clock-history" aria-hidden="true" /> Historial de moderación
      </h3>
      <div className="mt-3">
        {consulta.isPending ? (
          <SkeletonTarjetas n={1} />
        ) : consulta.isError ? (
          <EstadoError
            err={consulta.error}
            que="el historial de moderación"
            onReintentar={() => void consulta.refetch()}
          />
        ) : !filas.length ? (
          <p className="text-3 text-muted">Sin acciones de moderación registradas.</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {filas.map((h, i) => (
              <li
                key={`${h.created_at}-${h.entity_id}-${i}`}
                className="flex items-start gap-2 rounded-sm border border-line bg-sunken p-2"
              >
                <i
                  className={`bi ${ICONO_DE_ACCION[h.action] ?? 'bi-shield'} mt-0.5`}
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-3">
                    <strong>{h.moderator}</strong> {h.action_label} {h.entity_label} n.º{' '}
                    {h.entity_id}
                  </p>
                  {h.reason && (
                    <p className="text-2 text-muted">
                      <i className="bi bi-chat-quote" aria-hidden="true" /> {h.reason}
                    </p>
                  )}
                </div>
                <span className="whitespace-nowrap text-1 text-muted">
                  {Utils.relativeTime(h.created_at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function Palabras({ consulta }: { consulta: ReturnType<typeof useModeracion>['palabras'] }) {
  const agregar = useAgregarPalabra();
  const borrar = useBorrarPalabra();
  const [texto, setTexto] = useState('');

  const palabras = consulta.data ?? [];
  const deFabrica = palabras.filter((w) => w.de_fabrica).length;

  return (
    <section>
      <h3 className="flex items-center gap-2 text-3 font-bold text-brand-ink">
        <i className="bi bi-slash-circle" aria-hidden="true" /> Palabras bloqueadas
      </h3>
      <p className="mt-1 max-w-2xl text-2 text-muted">
        El sistema bloquea publicaciones y comentarios que contengan estas palabras. La
        comparación ignora acentos, mayúsculas y repeticiones. Las marcadas{' '}
        <strong>de fábrica</strong> vinieron con el sistema; <strong>puedes quitar
        cualquiera</strong> si da falsos positivos — por ejemplo <em>hp</em> en un texto sobre
        impresoras.
      </p>

      <form
        className="mt-3 flex max-w-md gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const limpio = texto.trim();
          if (!limpio) { Utils.toast('Escribe una palabra', 'warning'); return; }
          agregar.mutate(limpio, {
            onSuccess: () => { setTexto(''); Utils.toast('Palabra añadida', 'success'); },
            onError: (err) => Utils.toast((err as Error).message, 'error'),
          });
        }}
      >
        <InputSuelto
          className="flex-1"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Nueva palabra a bloquear"
          aria-label="Nueva palabra a bloquear"
        />
        <Button type="submit" loading={agregar.isPending}>
          <i className="bi bi-plus-lg" aria-hidden="true" /> Añadir
        </Button>
      </form>

      <div className="mt-3">
        {consulta.isPending ? (
          <SkeletonTarjetas n={1} />
        ) : consulta.isError ? (
          /* Con un `catch{}` vacío esto decía «La lista está vacía: no se bloquea
             ninguna palabra», que es lo contrario de lo que había pasado. */
          <EstadoError
            err={consulta.error}
            que="las palabras bloqueadas"
            onReintentar={() => void consulta.refetch()}
          />
        ) : !palabras.length ? (
          <p className="text-3 text-muted">La lista está vacía: no se bloquea ninguna palabra.</p>
        ) : (
          <>
            <p className="mb-2 text-2 text-muted">
              {palabras.length} {palabras.length === 1 ? 'palabra' : 'palabras'} en la lista
              {deFabrica ? ` · ${deFabrica} de fábrica` : ''}
            </p>
            <ul className="flex flex-wrap gap-2">
              {palabras.map((w) => (
                <li key={w.id}>
                  <span
                    className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-2 ${w.de_fabrica
                      ? 'border-line text-ink-2'
                      : 'border-danger text-danger-ink'}`}
                    title={w.de_fabrica
                      ? 'Vino con el sistema. Puedes quitarla.'
                      : 'Añadida por la administración.'}
                  >
                    {w.word}
                    <button
                      type="button"
                      className="rounded-full px-0.5 hover:bg-sunken focus-visible:focus-anillo"
                      onClick={() => borrar.mutate(w.id, {
                        onError: (e) => Utils.toast((e as Error).message, 'error'),
                      })}
                    >
                      <i className="bi bi-x-lg" aria-hidden="true" />
                      <span className="sr-only">Quitar «{w.word}» de la lista</span>
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </section>
  );
}

/** El motivo es obligatorio: queda en auditoría con el nombre de quien moderó. */
function PedirMotivo({ titulo, cargando, onCancelar, onConfirmar }: {
  titulo: string;
  cargando: boolean;
  onCancelar: () => void;
  onConfirmar: (motivo: string) => void;
}) {
  const [motivo, setMotivo] = useState('');
  const corto = motivo.trim().length < 5;

  return (
    <Dialog
      abierto
      onCambio={(v) => { if (!v) onCancelar(); }}
      titulo={titulo}
      descripcion="Queda registrado en auditoría con tu nombre y la fecha."
      pie={
        <>
          <Button variant="ghost" onClick={onCancelar}>Cancelar</Button>
          <Button
            variant="destructive"
            loading={cargando}
            disabled={corto}
            razonDeshabilitado="Indica un motivo de al menos cinco caracteres."
            onClick={() => onConfirmar(motivo.trim())}
          >
            <i className="bi bi-check2" aria-hidden="true" /> Confirmar
          </Button>
        </>
      }
    >
      <Field label="Motivo" ayuda="Mínimo cinco caracteres.">
        <Textarea
          rows={3}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Explica el motivo de esta acción"
        />
      </Field>
    </Dialog>
  );
}
